class GiftSetBuilder extends HTMLElement {
  connectedCallback() {
    this.requiredCount = parseInt(this.dataset.requiredCount, 10) || 3;
    this.propertyPrefix = this.dataset.propertyPrefix || 'Parfum';
    this.variantId = this.dataset.variantId;
    this.slots = Array(this.requiredCount).fill(null);
    this.activeSlot = 0;
    this.validated = false;

    this.slotElements = Array.from(this.querySelectorAll('[data-gift-set-slot]'));
    this.productButtons = Array.from(this.querySelectorAll('[data-gift-set-product]'));
    this.counterValue = this.querySelector('[data-gift-set-counter]');
    this.canvas = this.querySelector('[data-gift-set-canvas]');
    this.validatedView = this.querySelector('[data-gift-set-validated]');
    this.validateWrap = this.querySelector('[data-gift-set-validate-wrap]');
    this.editWrap = this.querySelector('[data-gift-set-edit-wrap]');
    this.validateButton = this.querySelector('[data-gift-set-validate]');
    this.editButtons = Array.from(this.querySelectorAll('[data-gift-set-edit], [data-gift-set-edit-validated]'));
    this.kitSummaries = Array.from(this.querySelectorAll('[data-gift-set-kit-summary]'));
    this.hasValidatedImage = Boolean(this.querySelector('.gift-set-builder__validated-image'));
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[data-gift-set-submit]');
    this.errorElement = this.querySelector('[data-gift-set-error]');

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    }

    this.bindEvents();
    this.render();
  }

  bindEvents() {
    this.slotElements.forEach((slot, index) => {
      slot.addEventListener('click', (event) => {
        if (this.validated || event.target.closest('[data-gift-set-slot-remove]')) {
          return;
        }

        this.activeSlot = index;
        this.render();
      });

      const removeButton = slot.querySelector('[data-gift-set-slot-remove]');

      if (removeButton) {
        removeButton.addEventListener('click', (event) => {
          event.stopPropagation();

          if (this.validated) {
            return;
          }

          this.slots[index] = null;
          this.activeSlot = index;
          this.render();
        });
      }
    });

    this.productButtons.forEach((button) => {
      const removeButton = button.querySelector('[data-gift-set-product-remove]');

      if (removeButton) {
        const removeProductSelection = (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (this.validated) {
            return;
          }

          const productTitle = button.dataset.title;
          const selectedIndex = this.findLastProductSlotIndex(productTitle);

          if (selectedIndex === -1) {
            return;
          }

          this.slots[selectedIndex] = null;
          this.activeSlot = selectedIndex;
          this.render();
        };

        removeButton.addEventListener('click', removeProductSelection);
        removeButton.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            removeProductSelection(event);
          }
        });
      }

      button.addEventListener('click', () => {
        if (this.validated) {
          return;
        }

        const product = {
          title: button.dataset.title,
          image: button.dataset.image,
          handle: button.dataset.handle,
          variantId: button.dataset.variantId
        };

        this.slots[this.activeSlot] = product;
        this.advanceActiveSlot();
        this.render();
      });
    });

    if (this.validateButton) {
      this.validateButton.addEventListener('click', () => {
        if (this.getFilledCount() < this.requiredCount) {
          return;
        }

        this.validated = true;
        this.showError('');
        this.render();
      });
    }

    if (this.editButtons.length) {
      this.editButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.validated = false;
          this.showError('');
          this.render();
        });
      });
    }
  }

  handleSubmit(event) {
    if (this.getFilledCount() < this.requiredCount) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.showError(this.dataset.incompleteMessage);
      return;
    }

    if (!this.validated) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.showError(this.dataset.validateFirstMessage);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.showError('');
    this.submitWithComponents();
  }

  buildVariantMap() {
    const map = {};

    this.slots.forEach((slot) => {
      if (!slot?.variantId) {
        return;
      }

      const variantId = String(slot.variantId);
      map[variantId] = (map[variantId] || 0) + 1;
    });

    return map;
  }

  formatVariantMap(variantMap) {
    return Object.entries(variantMap)
      .map(([variantId, quantity]) => `${variantId}:${quantity}`)
      .join(',');
  }

  buildGiftSetProperties(variantMap) {
    return {
      _gift_set: 'true',
      _gift_set_variants: this.formatVariantMap(variantMap)
    };
  }

  async submitWithComponents() {
    if (!this.form || !this.variantId) {
      return;
    }

    const variantMap = this.buildVariantMap();

    if (Object.keys(variantMap).length === 0) {
      this.showError('Impossible d\'ajouter au panier.');
      return;
    }

    const quantity = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10);
    const giftSetVariantId = String(this.variantId);
    const items = [{
      id: giftSetVariantId,
      quantity,
      properties: this.buildGiftSetProperties(variantMap)
    }];

    Object.entries(variantMap).forEach(([variantId, perSetQty]) => {
      items.push({
        id: variantId,
        quantity: perSetQty * quantity,
        properties: {
          _gift_set_component: 'true',
          _gift_set_parent: giftSetVariantId
        }
      });
    });

    const submitButtons = Array.from(this.form.elements).filter((button) => button.type === 'submit');
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute('disabled', 'disabled');
      submitButton.setAttribute('aria-busy', 'true');
    });
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
          sections_url: `${Shopify.routes.root}variants/${giftSetVariantId}`
        })
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute('disabled');
        submitButton.removeAttribute('aria-busy');
      });

      if (response.ok) {
        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

        if (responseJson.sections) {
          cartContent.sections = responseJson.sections;
        }

        const onSuccessDo = this.form.querySelector('[name="on_success"]')?.value;

        document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.items || [responseJson],
            cart: cartContent,
            onSuccessDo
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            onSuccessDo,
            cart: cartContent
          }
        }));

        if (!responseJson.sections) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }

        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          error: responseJson.description
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      this.showError(responseJson.description || 'Impossible d\'ajouter au panier.');
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute('disabled');
        submitButton.removeAttribute('aria-busy');
      });
      this.showError('Impossible d\'ajouter au panier.');
    }
  }

  advanceActiveSlot() {
    const nextEmpty = this.slots.findIndex((slot) => slot === null);

    if (nextEmpty !== -1) {
      this.activeSlot = nextEmpty;
      return;
    }

    this.activeSlot = Math.min(this.activeSlot, this.requiredCount - 1);
  }

  getFilledCount() {
    return this.slots.filter(Boolean).length;
  }

  getProductSelectionCount(title) {
    return this.slots.filter((slot) => slot && slot.title === title).length;
  }

  findLastProductSlotIndex(title) {
    for (let index = this.slots.length - 1; index >= 0; index -= 1) {
      const slot = this.slots[index];

      if (slot && slot.title === title) {
        return index;
      }
    }

    return -1;
  }

  formatSelectedLabel(count) {
    if (count <= 1) {
      return '';
    }

    const countLabel = this.dataset.selectedCountLabel || '×[count]';
    return countLabel.replace('[count]', String(count));
  }

  updateFormInputs() {
    if (!this.form) {
      return;
    }

    this.form.querySelectorAll('[data-gift-set-property]').forEach((input) => input.remove());
  }

  showError(message) {
    if (!this.errorElement) {
      return;
    }

    this.errorElement.textContent = message || '';
    this.errorElement.hidden = !message;
  }

  updateKitSummary() {
    const titles = this.slots.filter(Boolean).map((slot) => slot.title);

    this.kitSummaries.forEach((summary) => {
      const itemsElement = summary.querySelector('[data-gift-set-kit-summary-items]');

      if (!this.validated || titles.length === 0) {
        summary.hidden = true;
        return;
      }

      summary.hidden = false;

      if (!itemsElement) {
        return;
      }

      itemsElement.replaceChildren();

      titles.forEach((title, index) => {
        if (index > 0) {
          itemsElement.append(document.createTextNode(' · '));
        }

        const item = document.createElement('em');
        item.className = 'gift-set-builder__kit-summary-item';
        item.textContent = title;
        itemsElement.append(item);
      });
    });
  }

  render() {
    const filledCount = this.getFilledCount();
    const isComplete = filledCount === this.requiredCount;
    if (this.canvas) {
      this.canvas.hidden = this.validated && this.hasValidatedImage;
    }

    if (this.validatedView) {
      this.validatedView.hidden = !this.validated || !this.hasValidatedImage;
    }

    if (this.validateWrap) {
      this.validateWrap.hidden = !isComplete || this.validated;
    }

    if (this.editWrap) {
      this.editWrap.hidden = !this.validated || this.hasValidatedImage;
    }

    this.slotElements.forEach((slotElement, index) => {
      const selection = this.slots[index];
      const isFilled = Boolean(selection);

      slotElement.classList.toggle('is-filled', isFilled);
      slotElement.classList.toggle('is-active', !this.validated && this.activeSlot === index);

      const placeholder = slotElement.querySelector('[data-gift-set-slot-placeholder]');
      const selected = slotElement.querySelector('[data-gift-set-slot-selected]');
      const selectedImage = slotElement.querySelector('[data-gift-set-slot-selected-image]');

      if (placeholder) {
        placeholder.hidden = isFilled;
      }

      if (selected) {
        selected.hidden = !isFilled;
      }

      if (selectedImage) {
        if (selection) {
          selectedImage.src = selection.image;
          selectedImage.alt = selection.title;
        } else {
          selectedImage.removeAttribute('src');
          selectedImage.alt = '';
        }
      }
    });

    this.productButtons.forEach((button) => {
      const selectionCount = this.getProductSelectionCount(button.dataset.title);
      const isSelected = selectionCount > 0;
      button.disabled = this.validated;
      button.classList.toggle('is-selected', isSelected);
      button.querySelector('[data-gift-set-product-status]').textContent = this.formatSelectedLabel(selectionCount);
      const removeButton = button.querySelector('[data-gift-set-product-remove]');

      if (removeButton) {
        removeButton.hidden = !isSelected || this.validated;
      }
    });

    if (this.counterValue) {
      this.counterValue.textContent = `${filledCount} / ${this.requiredCount}`;
    }

    if (this.submitButton) {
      const canSubmit = isComplete && this.validated && Boolean(this.variantId);
      this.submitButton.disabled = !canSubmit;
      this.submitButton.setAttribute('aria-disabled', String(!canSubmit));
    }

    this.updateFormInputs();
    this.updateKitSummary();
  }
}

if (!window.customElements.get('gift-set-builder')) {
  window.customElements.define('gift-set-builder', GiftSetBuilder);
}
